import { useEffect, useMemo, useCallback } from 'react';
import { css } from '@emotion/react';
import { useNavigate } from '@tanstack/react-location';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { humanizeDateDifference } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { ContentLoader } from '@frontend/design-system';
import { URLs } from '../../../../constants';
import { useAnalyticsOrgLocations, useTimePeriodPopoverSelector } from '../../../../hooks';
import { trackingIds } from '../../../../tracking-ids';
import { CheckListPopoverSelector } from '../../../filter-selectors';
import { customROITimePeriods } from '../../constants';
import { usePrevious } from '../../hooks';
import { ROIFilters, useARRoiShallowStore } from '../hooks';

interface Props {
  isLoading?: boolean;
}

export const Filters = ({ isLoading }: Props) => {
  const { t } = useTranslation('analytics');
  const { selectedLocationIds } = useAppScopeStore();
  const navigate = useNavigate();
  const { isLoadingLocations, isMultiLocation, locationIds, locationNames } = useAnalyticsOrgLocations({
    module: 'ROI',
  });

  const {
    doNotOverrideFiltersOnLoad,
    filters,
    resetStore,
    setDefaultFilters,
    setDoNotOverrideFiltersOnLoad,
    setFilterHintText,
    setFilters,
  } = useARRoiShallowStore(
    'doNotOverrideFiltersOnLoad',
    'filters',
    'resetStore',
    'setDefaultFilters',
    'setDoNotOverrideFiltersOnLoad',
    'setFilterHintText',
    'setFilters'
  );

  const joinedSelectedLocationIds = useMemo(() => selectedLocationIds.sort().join(','), [selectedLocationIds]);
  const prevJoinedLocationIds = usePrevious(joinedSelectedLocationIds);
  const filteredLocationsCount = filters?.locationIds?.length || 0;

  const { defaultEndDate, defaultStartDate, selectedPeriod, TimePeriodPopoverSelector, timePeriods } =
    useTimePeriodPopoverSelector<typeof customROITimePeriods>({
      defaultPeriod: 'lastQuarter',
      timePeriods: customROITimePeriods,
      values: {
        startDate: filters?.startDate,
        endDate: filters?.endDate,
      },
    });

  const defaultFilters = useMemo(
    (): ROIFilters => ({
      endDate: defaultEndDate,
      locationIds,
      startDate: defaultStartDate,
    }),
    [defaultEndDate, defaultStartDate, locationIds]
  );

  const applyDefaultFilters = useCallback(() => {
    // Also used for resetting filters whenever required
    setDefaultFilters(defaultFilters);
    setFilters(defaultFilters);
  }, [defaultFilters, setDefaultFilters, setFilters]);

  // Effect to set the human-readable filter hint text
  useEffect(() => {
    if (filters?.startDate && filters.endDate) {
      setFilterHintText(
        selectedPeriod
          ? timePeriods?.[selectedPeriod]?.label
          : humanizeDateDifference(filters?.startDate, filters?.endDate)
      );
    }
  }, [filters?.startDate, filters?.endDate, selectedPeriod, setFilterHintText, timePeriods]);

  // Combined effect to handle initialization and synchronization with the global scope
  useEffect(() => {
    // Determine if the scope has genuinely changed since the last render
    const hasScopeChanged = !!prevJoinedLocationIds && prevJoinedLocationIds !== joinedSelectedLocationIds;

    if (hasScopeChanged) {
      // If the scope has changed, we must reset everything.
      resetStore();
      setDoNotOverrideFiltersOnLoad(false);
      applyDefaultFilters();

      if (selectedLocationIds.length === 1) {
        navigate({ to: URLs.ROI_APPOINTMENT_REMINDERS });
      }
      return;
    }

    // This section only runs if the scope has NOT changed.
    const areDefaultsReady =
      !!defaultFilters.startDate && !!defaultFilters.endDate && !!defaultFilters.locationIds?.length;
    if (!areDefaultsReady || doNotOverrideFiltersOnLoad) {
      // Don't do anything if defaults aren't ready or if we're meant to keep user-selected filters.
      return;
    }

    // Check if filters are already populated (covers soft navigation back to the page)
    const hasExistingFilters = filters?.startDate && filters?.endDate && filters?.locationIds?.length;
    if (!hasExistingFilters) {
      // Apply defaults only if no filters currently exist.
      applyDefaultFilters();
    }
  }, [
    applyDefaultFilters,
    defaultFilters,
    doNotOverrideFiltersOnLoad,
    filters,
    joinedSelectedLocationIds,
    navigate,
    prevJoinedLocationIds,
    resetStore,
    selectedLocationIds,
    setDoNotOverrideFiltersOnLoad,
  ]);

  return (
    <div css={styles.wrapper}>
      {isMultiLocation && (
        <CheckListPopoverSelector<string>
          defaultValue={defaultFilters.locationIds}
          disabled={isLoading}
          iconName='location-small'
          label={
            !filteredLocationsCount || filteredLocationsCount > 1
              ? t('{{filteredLocationsCount}} Locations', { filteredLocationsCount })
              : t('1 Location')
          }
          onChange={(locationIds) => {
            setFilters({ locationIds });
            setDoNotOverrideFiltersOnLoad(true);
          }}
          options={locationNames}
          value={filters?.locationIds}
          trackingId={trackingIds.roi.missedCallTextLocationPopoverFilter}
        />
      )}

      <TimePeriodPopoverSelector
        disabled={isLoading}
        onChange={({ startDate, endDate }) => {
          setFilters({ startDate, endDate });
          setDoNotOverrideFiltersOnLoad(true);
        }}
        showDateRangePicker={false}
        trackingIdBase={trackingIds.roi.missedCallTextTimePopoverFilter}
      />

      <ContentLoader show={isLoadingLocations} size='small' />
    </div>
  );
};

const styles = {
  wrapper: css`
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing(2)};
    position: relative;
  `,
};
